import { useState } from "react";
import "./Login.css";
import logo from "../../assets/images/logo.webp";
import ImageLogin from "../../assets/images/LoginImage.webp";
import { GoogleLogin } from "@react-oauth/google";
import { Eye, EyeOff, AlertCircle } from "lucide-react";

import { authFetch, httpClient } from '../../HttpClient';

import ForgotPasswordModal from "./ForgotPasswordModal";
function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  // Boîte mail Google partagée entre plusieurs profils (ex: secretariat@...)
  // — googleLogin() répond avec une liste de profils au lieu d'une session
  // directe ; il faut choisir le profil ET saisir SON mot de passe pour
  // confirmer avant d'entrer dans l'app.
  const [profileCandidates, setProfileCandidates] = useState(null);
  const [selectedProfileId, setSelectedProfileId] = useState("");
  const [profilePassword, setProfilePassword] = useState("");
  const [profileError, setProfileError] = useState(null);
  const [isConfirmingProfile, setIsConfirmingProfile] = useState(false);

  // Point d'entrée commun aux deux méthodes de connexion — écrit la session
  // (token + user) et prévient le parent, pour que les deux chemins restent
  // cohérents avec la persistance au refresh gérée dans App.js.
  const completeLogin = (token, user) => {
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(user));
    onLogin(user);
  };

  const handleLoginClick = async () => {
    if (isLoading) return;
    setError(null);
    setIsLoading(true);

    try {
      const response = await authFetch("https://backend-myalfa.vercel.app/api/users/auth",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        completeLogin(data.token, data.user);
      } else if (response.status === 403) {
        const data = await response.json().catch(() => ({}));
        setError(data.message || "Accès refusé.");
      } else {
        setError("Identifiants incorrects");
      }
    } catch (err) {
      setError("Erreur de connexion au serveur !");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleLoginClick();
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setError(null);
    try {
      const { data } = await httpClient.post("/api/users/google", {
        idToken: credentialResponse.credential,
      });
      if (data.requiresProfileSelection) {
        setProfileCandidates(data.candidates);
        setSelectedProfileId("");
        setProfilePassword("");
        setProfileError(null);
        return;
      }
      completeLogin(data.token, data.user);
    } catch (err) {
      setError(err.response?.data?.message || "Connexion Google refusée.");
    }
  };

  const handleConfirmProfile = async () => {
    if (!selectedProfileId) {
      setProfileError("Sélectionnez votre profil.");
      return;
    }
    if (!profilePassword) {
      setProfileError("Saisissez votre mot de passe.");
      return;
    }
    setIsConfirmingProfile(true);
    setProfileError(null);
    try {
      const { data } = await httpClient.post("/api/users/google/select-profile", {
        candidateUserId: selectedProfileId,
        password: profilePassword,
      });
      setProfileCandidates(null);
      completeLogin(data.token, data.user);
    } catch (err) {
      setProfileError(err.response?.data?.message || "Mot de passe incorrect.");
    } finally {
      setIsConfirmingProfile(false);
    }
  };

  const handleCancelProfileSelection = () => {
    setProfileCandidates(null);
    setSelectedProfileId("");
    setProfilePassword("");
    setProfileError(null);
  };

  return (
    <div className="LoginContainer">
      <form
        className="login"
        autoComplete="on"
        onSubmit={(e) => {
          e.preventDefault(); // empêche le rechargement
          handleLoginClick();
        }}
      >
        <img src={logo} alt="Logo" className="logoLogin" />
        <h2 style={{ margin: 0 }}>MyALFA Worksync</h2>
        <p
          style={{
            margin: 0,
            width: "50%",
            textAlign: "center",
            marginBottom: "4%",
            fontSize: "12px",
            color: "#840040",
          }}
        >
          Connectez-vous pour accéder à votre espace de travail.
        </p>
        {error && (
          <p className="TextInfo">
            <AlertCircle size={15} />
            {error}
          </p>
        )}

        <input
          type="email"
          name="email"
          autoComplete="email"
          placeholder="Adresse Email"
          value={email}
          onChange={(e) => setEmail(e.target.value.trim())}
          disabled={isLoading}
          className="inputEmail"
          style={{ marginBottom: "5%" }}
          onKeyDown={handleKeyDown}
        />

        <div
          className="inputEmail"
          style={{
            width: "60%",
            marginBottom: "5%",
            padding: 0,
            display: "flex",
            alignItems: "center",
          }}
        >
          <input
            type={showPassword ? "text" : "password"}
            name="password"
            autoComplete="current-password"
            placeholder="Mot de passe"
            value={password}
            onChange={(e) => setPassword(e.target.value.trim())}
            disabled={isLoading}
            style={{
              flex: 1,
              minWidth: 0,
              padding: "0.8rem",
              border: "none",
              outline: "none",
              background: "transparent",
              font: "inherit",
              color: "inherit",
            }}
            onKeyDown={handleKeyDown}
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            title={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
            style={{
              // width/margin-top explicitement écrasés : ".login button"
              // (règle générique de Login.css, pensée pour "Se connecter")
              // s'applique sinon aussi à ce bouton et le forçait à 60% de
              // large — c'est ce qui poussait l'icône au milieu du champ.
              width: "auto",
              flexShrink: 0,
              marginTop: 0,
              background: "none",
              border: "none",
              padding: "0 0.8rem",
              cursor: "pointer",
              color: "#8A7780",
              display: "flex",
              alignItems: "center",
            }}
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>

        {!isLoading && (
          <button type="submit" disabled={isLoading}>
            Se connecter
          </button>
        )}
        {isLoading && (
          <div className="loading-container-Login">
            <div className="spinner"></div>
            <p>Vérification des Identifiants ...</p>
          </div>
        )}
        <p
          style={{
            marginTop: "10px",
            fontSize: "12px",
            color: "#007bff",
            cursor: "pointer",
            textDecoration: "underline",
          }}
          onClick={() => setShowForgotPassword(true)}
        >
          Mot de passe oublié ?
        </p>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            width: "60%",
            margin: "4% 0",
            gap: "10px",
          }}
        >
          <div style={{ flex: 1, height: 1, background: "#ddd" }} />
          <span style={{ fontSize: "11px", color: "#999" }}>OU</span>
          <div style={{ flex: 1, height: 1, background: "#ddd" }} />
        </div>

        <GoogleLogin
          onSuccess={handleGoogleSuccess}
          onError={() => setError("Échec de connexion Google.")}
        />
      </form>

      <div className="ContainerLoginImage">
        <img src={ImageLogin} alt="Logo" className="ImageLogin" />
      </div>

      {showForgotPassword && (
        <ForgotPasswordModal initialEmail={email} onClose={() => setShowForgotPassword(false)} />
      )}

      {profileCandidates && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "14px",
              padding: "28px 26px",
              width: "90%",
              maxWidth: "380px",
              boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
            }}
          >
            <h3 style={{ margin: "0 0 6px 0", color: "#840040", fontSize: "17px" }}>
              Qui êtes-vous ?
            </h3>
            <p style={{ margin: "0 0 18px 0", fontSize: "12.5px", color: "#777" }}>
              Cette boîte mail est partagée. Sélectionnez votre profil et saisissez
              votre mot de passe pour continuer.
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "16px" }}>
              {profileCandidates.map((c) => (
                <label
                  key={c.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: `1.5px solid ${String(selectedProfileId) === String(c.id) ? "#840040" : "#e5e5e5"}`,
                    cursor: "pointer",
                    fontSize: "14px",
                    color: "#333",
                  }}
                >
                  <input
                    type="radio"
                    name="profileCandidate"
                    value={c.id}
                    checked={String(selectedProfileId) === String(c.id)}
                    onChange={(e) => setSelectedProfileId(e.target.value)}
                  />
                  {c.name} {c.lastName}
                </label>
              ))}
            </div>

            <input
              type="password"
              placeholder="Votre mot de passe"
              value={profilePassword}
              onChange={(e) => setProfilePassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleConfirmProfile()}
              disabled={isConfirmingProfile}
              style={{
                width: "100%",
                boxSizing: "border-box",
                padding: "0.7rem 0.9rem",
                borderRadius: "8px",
                border: "1px solid #ddd",
                fontSize: "14px",
                marginBottom: "10px",
              }}
            />

            {profileError && (
              <p style={{ color: "#c0392b", fontSize: "12.5px", margin: "0 0 10px 0" }}>
                {profileError}
              </p>
            )}

            <div style={{ display: "flex", gap: "10px" }}>
              <button
                type="button"
                onClick={handleCancelProfileSelection}
                disabled={isConfirmingProfile}
                style={{
                  flex: 1,
                  padding: "0.6rem",
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                  background: "#fff",
                  color: "#666",
                  cursor: "pointer",
                }}
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleConfirmProfile}
                disabled={isConfirmingProfile}
                style={{
                  flex: 1,
                  padding: "0.6rem",
                  borderRadius: "8px",
                  border: "none",
                  background: "#840040",
                  color: "#fff",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                {isConfirmingProfile ? "Vérification..." : "Continuer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Login;
